// Fluent UI Imports
import { Checkbox, Stack } from '@fluentui/react';

// React Imports
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

// ALP Component Imports
import { RuleExpressionController } from './RuleExpressionController.Component';
import { RuleExpressionGroupCollection } from './RuleExpressionGroupCollection.Component';
import { RuleExpressionRow } from './RuleExpressionRow.Component';

// ALP Type Imports
import { CollectionOperators, INodeExpression, LogicalOperators, RelationalOperators } from '../common/Types';
import { RuleExpressions } from '../common/Helpers';

interface Props {
  expression: INodeExpression,
  jsonSchema: string,
  jsonSchemaRootPath: string,
  lhsDropdownFilter: string | null,
  checkboxVisible: boolean,
  onCheck?: (isChecked: boolean) => void,
  onChange: (expression: any) => void
}

export const RuleExpressionGroupLogical: React.FC<Props> = (props) => {

  // #region States

  const [forceRedraw, setForceRedraw] = useState<boolean>(false);
  const [expression, setExpression] = useState<INodeExpression | undefined>(props.expression);
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);

  // #endregion States

  // #region Event Handlers

  /**
   * Event handler for when '+ Rule' button is clicked at the group controller/header.
   */
  const onAddRule = (): void => {
    if (!expression) {
      setExpression(RuleExpressions.getDefaultRule());
    } else if ((expression as INodeExpression).Operator) {
      const op = (expression as INodeExpression).Operator;
      if (RelationalOperators.some((o) => o.key === op)) {
        setExpression({
          key: uuidv4(),
          Operator: 'and',
          Operands: [
            expression,
            RuleExpressions.getDefaultRule()]
        });
      } else if (LogicalOperators.some((o) => o.key === op)) {
        expression.Operands.push(RuleExpressions.getDefaultRule());
        setExpression(expression);
      } else {
        console.log('bad expression');
      }
    } else {
      console.log('bad expression');
    }

    setForceRedraw(!forceRedraw);
  };

  /**
   * Event handler for when '+ Group' button is clicked at the group controller/header.
   */
  const onAddGroup = (): void => {
    const newOperands = (expression as INodeExpression).Operands.filter(o => checkedKeys.indexOf(o.key) === -1);
    newOperands.push({
      key: uuidv4(),
      Operator: 'and',
      Operands: (expression as INodeExpression).Operands.filter(o => checkedKeys.indexOf(o.key) > -1)
    });

    setExpression({
      key: (expression as INodeExpression).key,
      Operator: (expression as INodeExpression).Operator,
      Operands: newOperands
    });

    setCheckedKeys([]);
    setForceRedraw(!forceRedraw);
  };

  /**
   * Event handler for one element change in the current collection.
   * (One row of expression in the group or the entire group).
   *
   * @param {string} elId - The id/key of the element which got changed.
   * @param {any} updatedElement - The updated element.
   */
  const onElementChange = (elId: string, updatedElement: any | undefined): void => {
    if (!updatedElement) {
      if (elId === expression?.key) {
        setExpression(undefined);
      } else {
        const updatedExpression = RuleExpressions.deepDelete(expression, 'key', elId) as INodeExpression;
        if (updatedExpression.Operands.length === 0) {
          setExpression(undefined);
        } else if (updatedExpression.Operands.length === 1) {
          props.onChange(updatedExpression.Operands[0]);
        } else {
          setExpression(updatedExpression);
        }
      }
    } else {
      const updatedExpression = RuleExpressions.deepReplaceObject(expression, 'key', elId, updatedElement);
      setExpression(updatedExpression);
    }

    setForceRedraw(!forceRedraw);
  };

  /**
   * Event handler for one element checked/unchecked in the current collection.
   * (Only individual rows, not the group).
   *
   * @param {string} key - The id/key of the element which got changed.
   * @param {boolean} isChecked - Boolean indicating if the element got checked or unchecked.
   */
  const onElementChecked = (key: string, isChecked: boolean): void => {
    if (isChecked) {
      setCheckedKeys([...checkedKeys, key]);
    } else {
      setCheckedKeys(checkedKeys.filter(k => k !== key));
    }
  };

  /**
   * Event handler for root operator change for the collection that this group component is rendering.
   *
   * @param {string} elId - The id/key of the element which got changed.
   * @param {boolean} value - The new operator key for the operators dropdown.
   */
  const onOpChange = (elId: string, value: string | number | undefined): void => {
    const updatedExpression = RuleExpressions.deepReplaceProperty(expression, 'key', elId, 'Operator', value as string);
    setExpression(updatedExpression);
    setForceRedraw(!forceRedraw);
  };

  // #endregion Event Handlers

  // #region Render

  useEffect(() => {
    props.onChange(expression);
  }, [expression, forceRedraw]);

  const renderElement = (e: INodeExpression): JSX.Element => {
    if (RelationalOperators.some((o) => o.key === e.Operator)) {
      return (
        <RuleExpressionRow
          key={ e.key }
          expression={ e as INodeExpression }
          jsonSchema={ props.jsonSchema }
          jsonSchemaRootPath={ props.jsonSchemaRootPath }
          lhsDropdownFilter={ props.lhsDropdownFilter }
          onCheck={ (c: boolean) => { onElementChecked((e as INodeExpression).key, c); } }
          onChange={ (ue: any | undefined) => { onElementChange((e as INodeExpression).key, ue); } }>
        </RuleExpressionRow>);
    } else if (CollectionOperators.some((o) => o.key === e.Operator)) {
      return (
        <RuleExpressionGroupCollection
          key={ e.key }
          expression={ e as INodeExpression }
          jsonSchema={ props.jsonSchema }
          jsonSchemaRootPath={ props.jsonSchemaRootPath }
          checkboxVisible={ true }
          lhsDropdownFilter={ props.lhsDropdownFilter }
          onCheck={ (c: boolean) => { onElementChecked((e as INodeExpression).key, c); } }
          onChange={ (ue: any | undefined) => { onElementChange((e as INodeExpression).key, ue); } }>
        </RuleExpressionGroupCollection>);
    } else if (LogicalOperators.some((o) => o.key === e.Operator)) {
      return (
        <RuleExpressionGroupLogical
          key={ e.key }
          expression={ e as INodeExpression }
          jsonSchema={ props.jsonSchema }
          jsonSchemaRootPath={ props.jsonSchemaRootPath }
          checkboxVisible={ true }
          lhsDropdownFilter={ props.lhsDropdownFilter }
          onCheck={ (c: boolean) => { onElementChecked((e as INodeExpression).key, c); } }
          onChange={ (ue: any | undefined) => { onElementChange((e as INodeExpression).key, ue); } }>
        </RuleExpressionGroupLogical>);
    } else {
      return (<></>);
    }
  };

  return (
    <>
      {
        expression
          ? <Stack horizontal tokens={ { childrenGap: 10 } } verticalAlign='center'>
            {
              props.checkboxVisible
                ? <Checkbox
                  onChange={ (e?: any, checked?: boolean) => {
                    if (props.onCheck) {
                      props.onCheck(checked ?? false);
                    }
                  } }>
                </Checkbox>
                : <></>
            }
            <Stack tokens={ { childrenGap: 10 } } style={ { padding: 20, border: '1px solid black' } } verticalAlign='center'>
              <RuleExpressionController
                isRoot={ false }
                jsonSchema={ props.jsonSchema }
                jsonSchemaRootPath={ props.jsonSchemaRootPath }
                collectionDropdownFilter={ props.lhsDropdownFilter }
                onAddRule={ onAddRule }
                onAddGroup={ onAddGroup }
                addGroupEnabled={ checkedKeys.length > 1 }
                logicalOperator={ (expression as INodeExpression).Operator }
                onLogicalOpChange={ (op: string | number | undefined) => onOpChange((expression as INodeExpression).key, op) }
              />
              {
                (expression as INodeExpression).Operands.map(o => {
                  return renderElement(o as INodeExpression);
                })
              }
            </Stack>
          </Stack>
          : <></>
      }
    </>
  );

  // #endregion Render
};
